import { UseMutationOptions, useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';

import {
  CaseGetFilterDto,
  CaseHistoryResponseDto,
  CaseResponseDto,
  CaseBulkUpdateDto,
  CreateCaseHistoryDto,
  CreateEmailDto,
  CreateNoteDto,
  RelatedCasesListItem,
  PagedResponse,
  GetFilterByIdDto,
  FieldBlockSchemaDto,
  CaseStatisticsResponseDto,
  GetNeedAgentStatsDto,
  GetNeedAgentStatsResponseDto,
  GetCasesByPeriodsDto,
  CasesByPeriodsResponseDto,
} from '@bandido/shared/dtos';
import { DayOfWeek, CasesByDayAndHourResponseDto } from '@frontend/features/dashboard/components/TrendsSection';
import { QueryKeys } from '../common/types';
import { client } from '../common/services';

interface ThreadEmail {
  id: number;
  from: string;
  subject: string;
  original_content: string;
  original_text: string;
  content: string;
  created_at: string;
}

interface useGetAllCasesProps extends CaseGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  customDateFieldFilters?: { fieldName: string; after?: string; before?: string }[];
}

export interface NewCaseHistoryDto extends Omit<CreateCaseHistoryDto, 'note' | 'email'> {
  note?: Omit<CreateNoteDto, 'history'>;
  email?: Omit<CreateEmailDto, 'history'>;
}

const getCaseHistory = (id: number, params: Record<string, unknown>, currentPage: number) =>
  client.get<PagedResponse<CaseHistoryResponseDto>>(`/case/${id}/history`, {
    params: {
      currentPage,
      pageSize: 30,
      ...params,
    },
  });

const getEmailThread = (emailId: number) => client.get<ThreadEmail[]>(`emails/${emailId}/thread`);

const bulkUpdateCases = (payload: CaseBulkUpdateDto) => client.patch('case/bulk', payload);

const getCases = async ({
  teamIds,
  search,
  sort,
  order,
  workflowIds,
  clientIds,
  stepIds,
  agentIds,
  agencies,
  hasClient,
  originatedByChannels,
  customFieldFilters,
  currentPage,
  status,
  pageSize = 10,
  createdAfter,
  createdBefore,
  updatedAfter,
  updatedBefore,
  customDateFieldFilters,
}: useGetAllCasesProps) => {
  const params: Record<string, unknown> = {
    currentPage,
    pageSize,
    search,
    sort,
    order,
  };

  // Agregar filtros de fecha si existen
  if (createdAfter) params.createdAfter = createdAfter;
  if (createdBefore) params.createdBefore = createdBefore;
  if (updatedAfter) params.updatedAfter = updatedAfter;
  if (updatedBefore) params.updatedBefore = updatedBefore;
  if (typeof hasClient === 'boolean') params.hasClient = hasClient;

  // Agregar custom date field filters
  if (customDateFieldFilters && customDateFieldFilters.length > 0) {
    customDateFieldFilters.forEach((filter) => {
      if (filter.after) {
        params[`${filter.fieldName}_after`] = filter.after;
      }
      if (filter.before) {
        params[`${filter.fieldName}_before`] = filter.before;
      }
    });
  }

  const queryParams = [
    {
      name: 'teamIds',
      value: teamIds,
    },
    {
      name: 'workflowIds',
      value: workflowIds,
    },
    {
      name: 'stepIds',
      value: stepIds,
    },
    {
      name: 'clientIds',
      value: clientIds,
    },
    {
      name: 'agentIds',
      value: agentIds,
    },
    {
      name: 'agencies',
      value: agencies,
    },
    {
      name: 'status',
      value: status,
    },
    {
      name: 'originatedByChannels',
      value: originatedByChannels,
    },
  ];

  if (customFieldFilters && customFieldFilters.length > 0) {
    customFieldFilters.forEach((filter) => {
      params[filter.fieldName] = filter.value;
    });
  }
  getRequestParams(queryParams, params);

  return await client.get<PagedResponse<CaseResponseDto>>('case', {
    params,
  });
};

export const downloadCaseAttachment = (fileId: number) =>
  client.get(`/case-history/files/${fileId}`, { responseType: 'blob' }).then((response) => {
    const type: string = response.headers['content-type'];
    const url = window.URL.createObjectURL(
      new Blob([response.data], {
        type: type,
      }),
    );
    if (type.startsWith('audio/')) {
      return url;
    }
    window.open(url);
  });

const createCaseHistoryItem = (data: NewCaseHistoryDto) => {
  const { attachments, ...historyData } = data;
  const formData = new FormData();
  formData.append('body', JSON.stringify(historyData));
  for (const file of attachments || []) {
    formData.append('attachments', file as unknown as Blob);
  }
  return client.post('case-history', formData);
};

const replyToHistoryEmail = (id: number, data: NewCaseHistoryDto) => {
  const { attachments, ...historyData } = data;
  const formData = new FormData();
  formData.append('body', JSON.stringify(historyData));
  for (const file of attachments || []) {
    formData.append('attachments', file as unknown as Blob);
  }
  return client.post(`case-history/${id}/email-reply`, formData);
};

const getRequestParams = (
  arr: Array<{ value?: string[] | number[] | 'notselected'[] | (number | 'notselected')[]; name: string }>,
  params: Record<string, unknown>,
) => {
  arr.forEach((param) => {
    if (param.value && param.value.length > 0) {
      params[param.name] = param.value.join('|');
    }
  });

  return params;
};

export const useCaseSavedFilters = ({ onErrorCallback }: { onErrorCallback?: (val: unknown) => void }) =>
  useQuery({
    queryKey: [QueryKeys.getCaseSavedFilters],
    queryFn: async (): Promise<
      | {
          publicFilters: GetFilterByIdDto[];
          privateFilters: GetFilterByIdDto[];
        }
      | undefined
    > => {
      try {
        const filters: AxiosResponse<{
          publicFilters: GetFilterByIdDto[];
          privateFilters: GetFilterByIdDto[];
        }> = await client.get('filters');

        return filters.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e);
      }
    },
    refetchOnWindowFocus: true,
  });

export const useGetAllCases = ({ onErrorCallback, ...params }: useGetAllCasesProps) =>
  useQuery({
    queryKey: [QueryKeys.getAllCases, params],
    queryFn: () => getCases(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
  });

export const useGetAllCasesInfinity = (params: useGetAllCasesProps) =>
  useInfiniteQuery({
    queryKey: [QueryKeys.getInfinityCases, params],
    queryFn: async ({ pageParam = 1 }) => getCases({ ...params, currentPage: pageParam }),
    getNextPageParam: (lastPage) => {
      if (Number(lastPage?.data?.currentPage) < Number(lastPage?.data?.pages)) {
        return Number(lastPage?.data.currentPage) + 1;
      }
    },
  });

interface UseGetCaseById {
  id?: number;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetCaseById = ({ id, onErrorCallback }: UseGetCaseById) =>
  useQuery({
    queryKey: [QueryKeys.getCaseById, id],
    queryFn: async (): Promise<CaseResponseDto | undefined> => {
      try {
        const result: AxiosResponse<CaseResponseDto, any> = await client.get(`case/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

export const useGetRelatedCases = ({ id, onErrorCallback }: UseGetCaseById) =>
  useQuery({
    queryKey: [QueryKeys.getRelatedCases, id],
    queryFn: async (): Promise<RelatedCasesListItem[] | undefined> => {
      try {
        const result: AxiosResponse<RelatedCasesListItem[], any> = await client.get(`case/${id}/related`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

export const useGetCaseHistory = (id: number, params: Record<string, unknown>) =>
  useInfiniteQuery(
    [QueryKeys.getCaseHistory, { caseId: id, ...params }],
    ({ pageParam = 1 }) => getCaseHistory(id, params, pageParam),
    {
      enabled: Boolean(id),
      select: (data) => ({
        pages: [...data.pages].reverse(),
        pageParams: [...data.pageParams].reverse(),
      }),
      getNextPageParam: (lastPage) => {
        if (Number(lastPage.data.currentPage) < Number(lastPage.data.pages)) {
          return Number(lastPage.data.currentPage) + 1;
        }
      },
    },
  );

interface UseGetEmailThreadProps {
  emailId: number;
  enabled?: boolean;
  onErrorCallback?: (e: AxiosError) => void;
}

export const useGetEmailThread = ({ emailId, enabled = true, onErrorCallback }: UseGetEmailThreadProps) =>
  useQuery({
    queryKey: [QueryKeys.getEmailThread, emailId],
    queryFn: () => getEmailThread(emailId),
    enabled: Boolean(emailId) && enabled,
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
  });

export const useCreateCaseHistoryItem = (
  options?:
    | Omit<UseMutationOptions<AxiosResponse<any, any>, unknown, NewCaseHistoryDto, unknown>, 'mutationFn'>
    | undefined,
) => useMutation(createCaseHistoryItem, options);

export const useReplyToEmail = (
  id: number,
  options?:
    | Omit<UseMutationOptions<AxiosResponse<NewCaseHistoryDto, any>, unknown, NewCaseHistoryDto, unknown>, 'mutationFn'>
    | undefined,
) => useMutation((data) => replyToHistoryEmail(id, data), options);

export const useBulkUpdateCases = (
  options?:
    | Omit<
        UseMutationOptions<AxiosResponse<{ updatedCount: number }, any>, unknown, CaseBulkUpdateDto, unknown>,
        'mutationFn'
      >
    | undefined,
) => useMutation(bulkUpdateCases, options);

interface UseGseGetCaseCustomFieldsProps {
  id?: string;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetCaseCustomFields = ({ id, onErrorCallback }: UseGseGetCaseCustomFieldsProps) =>
  useQuery({
    queryKey: [QueryKeys.getCaseCustomFields, id],
    queryFn: async (): Promise<FieldBlockSchemaDto[] | undefined> => {
      try {
        const result: AxiosResponse<FieldBlockSchemaDto[], any> = await client.get(`case/${id}/custom-fields`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

interface CasesStatsByDay {
  date: string;
  count: number;
}

interface CasesStatsByTeam {
  teamId: number;
  teamName: string;
  count: number;
}

interface CasesStatsByStatus {
  status: string;
  count: number;
}

interface CasesStatsByWorkflow {
  workflowId: number;
  workflowName: string;
  count: number;
}

export const useGetCasesStatsByDay = (onErrorCallback?: (_e: AxiosError) => void) =>
  useQuery({
    queryKey: [QueryKeys.getCasesStatsByDay],
    queryFn: async (): Promise<CasesStatsByDay[] | undefined> => {
      try {
        const result: AxiosResponse<CasesStatsByDay[]> = await client.get('case/stats/by-day');
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });

export const useGetCasesStatsByTeam = (onErrorCallback?: (_e: AxiosError) => void) =>
  useQuery({
    queryKey: [QueryKeys.getCasesStatsByTeam],
    queryFn: async (): Promise<CasesStatsByTeam[] | undefined> => {
      try {
        const result: AxiosResponse<CasesStatsByTeam[]> = await client.get('case/stats/by-team');
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });

export const useGetCasesStatsByStatus = (onErrorCallback?: (_e: AxiosError) => void) =>
  useQuery({
    queryKey: [QueryKeys.getCasesStatsByStatus],
    queryFn: async (): Promise<CasesStatsByStatus[] | undefined> => {
      try {
        const result: AxiosResponse<CasesStatsByStatus[]> = await client.get('case/stats/by-status');
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });

export const useGetCasesStatsByWorkflow = (onErrorCallback?: (_e: AxiosError) => void) =>
  useQuery({
    queryKey: [QueryKeys.getCasesStatsByWorkflow],
    queryFn: async (): Promise<CasesStatsByWorkflow[] | undefined> => {
      try {
        const result: AxiosResponse<CasesStatsByWorkflow[]> = await client.get('case/stats/by-workflow');
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });

interface useGetCasesStatsProps extends CaseGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  customDateFieldFilters?: { fieldName: string; after?: string; before?: string }[];
}

const getCasesStatistics = async ({
  teamIds,
  search,
  sort,
  order,
  workflowIds,
  clientIds,
  stepIds,
  agentIds,
  originatedByChannels,
  customFieldFilters,
  currentPage,
  status,
  pageSize = 10,
  createdAfter,
  createdBefore,
  updatedAfter,
  updatedBefore,
  customDateFieldFilters,
}: useGetCasesStatsProps) => {
  const params: Record<string, unknown> = {
    currentPage,
    pageSize,
    search,
    sort,
    order,
  };

  // Agregar filtros de fecha si existen
  if (createdAfter) params.createdAfter = createdAfter;
  if (createdBefore) params.createdBefore = createdBefore;
  if (updatedAfter) params.updatedAfter = updatedAfter;
  if (updatedBefore) params.updatedBefore = updatedBefore;

  // Agregar custom date field filters
  if (customDateFieldFilters && customDateFieldFilters.length > 0) {
    customDateFieldFilters.forEach((filter) => {
      if (filter.after) {
        params[`${filter.fieldName}_after`] = filter.after;
      }
      if (filter.before) {
        params[`${filter.fieldName}_before`] = filter.before;
      }
    });
  }

  const queryParams = [
    { name: 'teamIds', value: teamIds },
    { name: 'workflowIds', value: workflowIds },
    { name: 'stepIds', value: stepIds },
    { name: 'clientIds', value: clientIds },
    { name: 'agentIds', value: agentIds },
    { name: 'status', value: status },
    { name: 'originatedByChannels', value: originatedByChannels },
  ];

  if (customFieldFilters && customFieldFilters.length > 0) {
    customFieldFilters.forEach((filter) => {
      params[filter.fieldName] = filter.value;
    });
  }

  getRequestParams(queryParams, params);

  return client.get<CaseStatisticsResponseDto>('case/statistics', { params });
};

export const useGetCasesStatistics = ({ onErrorCallback, enabled = true, ...params }: useGetCasesStatsProps) =>
  useQuery({
    queryKey: [QueryKeys.getCasesStatistics, params],
    queryFn: () => getCasesStatistics(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

interface UseGetNeedAgentStatsProps extends GetNeedAgentStatsDto {
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

const getNeedAgentStats = async (params: GetNeedAgentStatsDto) => {
  const queryParams: Record<string, string> = {};

  if (params.agencyIds && params.agencyIds.length > 0) {
    queryParams.agencyIds = params.agencyIds.join(',');
  }

  if (params.channels && params.channels.length > 0) {
    queryParams.channels = params.channels.join(',');
  }

  if (params.teams && params.teams.length > 0) {
    queryParams.teams = params.teams.join(',');
  }

  if (params.startDate) {
    queryParams.startDate = params.startDate;
  }

  if (params.endDate) {
    queryParams.endDate = params.endDate;
  }

  return client.get<GetNeedAgentStatsResponseDto>('case/need-agent-stats', {
    params: queryParams,
  });
};

export const useGetNeedAgentStats = ({ onErrorCallback, enabled = true, ...params }: UseGetNeedAgentStatsProps) =>
  useQuery({
    queryKey: [QueryKeys.getNeedAgentStats, params],
    queryFn: () => getNeedAgentStats(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

interface UseGetCasesByPeriodsProps extends GetCasesByPeriodsDto {
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

const getCasesByPeriods = async (params: GetCasesByPeriodsDto) => {
  const queryParams: Record<string, string> = {};

  if (params.agencyIds && params.agencyIds.length > 0) {
    queryParams.agencyIds = params.agencyIds.join(',');
  }

  if (params.channels && params.channels.length > 0) {
    queryParams.channels = params.channels.join(',');
  }

  if (params.teams && params.teams.length > 0) {
    queryParams.teams = params.teams.join(',');
  }

  return client.get<CasesByPeriodsResponseDto>('case/cases-by-periods', {
    params: queryParams,
  });
};

export const useGetCasesByPeriods = ({ onErrorCallback, enabled = true, ...params }: UseGetCasesByPeriodsProps) =>
  useQuery({
    queryKey: [QueryKeys.getCasesByPeriods, params],
    queryFn: () => getCasesByPeriods(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

interface UseGetCasesResolvedByPeriodsProps extends GetCasesByPeriodsDto {
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

const getCasesResolvedByPeriods = async (params: GetCasesByPeriodsDto) => {
  const queryParams: Record<string, string> = {};

  if (params.agencyIds && params.agencyIds.length > 0) {
    queryParams.agencyIds = params.agencyIds.join(',');
  }

  if (params.channels && params.channels.length > 0) {
    queryParams.channels = params.channels.join(',');
  }

  if (params.teams && params.teams.length > 0) {
    queryParams.teams = params.teams.join(',');
  }

  return client.get<CasesByPeriodsResponseDto>('case/cases-resolved-by-periods', {
    params: queryParams,
  });
};

export const useGetCasesResolvedByPeriods = ({
  onErrorCallback,
  enabled = true,
  ...params
}: UseGetCasesResolvedByPeriodsProps) =>
  useQuery({
    queryKey: [QueryKeys.getCasesResolvedByPeriods, params],
    queryFn: () => getCasesResolvedByPeriods(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

interface UseGetCasesNeedAgentByTimeOpenProps extends GetCasesByPeriodsDto {
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

const getCasesNeedAgentByTimeOpen = async (params: GetCasesByPeriodsDto) => {
  const queryParams: Record<string, string> = {};

  if (params.agencyIds && params.agencyIds.length > 0) {
    queryParams.agencyIds = params.agencyIds.join(',');
  }

  if (params.channels && params.channels.length > 0) {
    queryParams.channels = params.channels.join(',');
  }

  if (params.teams && params.teams.length > 0) {
    queryParams.teams = params.teams.join(',');
  }

  return client.get<{
    oneHourOrLess: number;
    oneToThreeHours: number;
    threeToEightHours: number;
    eightToTwentyFourHours: number;
    twentyFourToFortyEightHours: number;
    moreThanFortyEightHours: number;
  }>('case/cases-need-agent-by-time-open', {
    params: queryParams,
  });
};

export const useGetCasesNeedAgentByTimeOpen = ({
  onErrorCallback,
  enabled = true,
  ...params
}: UseGetCasesNeedAgentByTimeOpenProps) =>
  useQuery({
    queryKey: [QueryKeys.getCasesNeedAgentByTimeOpen, params],
    queryFn: () => getCasesNeedAgentByTimeOpen(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

interface UseGetImportantNumbersProps {
  agencyIds: number[];
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

const getImportantNumbers = async (agencyIds: number[]) => {
  const queryParams: Record<string, string> = {};

  if (agencyIds && agencyIds.length > 0) {
    queryParams.agencyIds = agencyIds.join(',');
  }

  return client.get<{
    availableAgentsCount: number;
    needAgentCount: number;
    callsOnHoldCount: number;
    avgEmailResponseTime: number;
    avgWhatsappResponseTime: number;
  }>('case/important-numbers', {
    params: queryParams,
  });
};

export const useGetImportantNumbers = ({ onErrorCallback, enabled = true, ...params }: UseGetImportantNumbersProps) =>
  useQuery({
    queryKey: [QueryKeys.getImportantNumbers, params],
    queryFn: () => getImportantNumbers(params.agencyIds),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

interface UseGetCasesByDayOfWeekProps {
  agencyIds: number[];
  startDate: string;
  endDate: string;
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

const getCasesByDayOfWeek = async (params: Omit<UseGetCasesByDayOfWeekProps, 'onErrorCallback' | 'enabled'>) => {
  const queryParams: Record<string, string> = {};

  if (params.agencyIds && params.agencyIds.length > 0) {
    queryParams.agencyIds = params.agencyIds.join(',');
  }

  if (params.startDate) {
    queryParams.startDate = params.startDate;
  }

  if (params.endDate) {
    queryParams.endDate = params.endDate;
  }

  return client.get<{
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  }>('case/cases-by-day-of-week', {
    params: queryParams,
  });
};

export const useGetCasesByDayOfWeek = ({ onErrorCallback, enabled = true, ...params }: UseGetCasesByDayOfWeekProps) =>
  useQuery({
    queryKey: [QueryKeys.getCasesByDayOfWeek, params],
    queryFn: () => getCasesByDayOfWeek(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

interface UseGetCasesByDayAndHourProps {
  agencyIds: number[];
  startDate: string;
  endDate: string;
  dayOfWeek: DayOfWeek;
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

const getCasesByDayAndHour = async (params: UseGetCasesByDayAndHourProps) => {
  const { agencyIds, startDate, endDate, dayOfWeek } = params;

  const queryParams: Record<string, unknown> = {
    startDate,
    endDate,
    dayOfWeek,
  };

  if (agencyIds && agencyIds.length > 0) {
    queryParams.agencyIds = agencyIds.join(',');
  }

  return client.get<CasesByDayAndHourResponseDto[]>('case/cases-by-day-and-hour', { params: queryParams });
};

export const useGetCasesByDayAndHour = ({ onErrorCallback, enabled = true, ...params }: UseGetCasesByDayAndHourProps) =>
  useQuery({
    queryKey: [QueryKeys.getCasesByDayAndHour, params],
    queryFn: () => getCasesByDayAndHour(params),
    onError: (e) => {
      onErrorCallback && onErrorCallback(e as AxiosError);
    },
    select: ({ data }) => data,
    refetchOnWindowFocus: true,
    enabled,
  });

const exportCasesCsv = async ({
  teamIds,
  search,
  sort,
  order,
  workflowIds,
  clientIds,
  stepIds,
  agentIds,
  originatedByChannels,
  customFieldFilters,
  status,
  createdAfter,
  createdBefore,
  updatedAfter,
  updatedBefore,
  customDateFieldFilters,
}: Omit<useGetAllCasesProps, 'currentPage' | 'pageSize' | 'onErrorCallback'>) => {
  const params: Record<string, unknown> = {
    search,
    sort,
    order,
  };

  // Agregar filtros de fecha si existen
  if (createdAfter) params.createdAfter = createdAfter;
  if (createdBefore) params.createdBefore = createdBefore;
  if (updatedAfter) params.updatedAfter = updatedAfter;
  if (updatedBefore) params.updatedBefore = updatedBefore;

  // Agregar custom date field filters
  if (customDateFieldFilters && customDateFieldFilters.length > 0) {
    customDateFieldFilters.forEach((filter) => {
      if (filter.after) {
        params[`${filter.fieldName}_after`] = filter.after;
      }
      if (filter.before) {
        params[`${filter.fieldName}_before`] = filter.before;
      }
    });
  }

  const queryParams = [
    { name: 'teamIds', value: teamIds },
    { name: 'workflowIds', value: workflowIds },
    { name: 'stepIds', value: stepIds },
    { name: 'clientIds', value: clientIds },
    { name: 'agentIds', value: agentIds },
    { name: 'status', value: status },
    { name: 'originatedByChannels', value: originatedByChannels },
  ];

  if (customFieldFilters && customFieldFilters.length > 0) {
    customFieldFilters.forEach((filter) => {
      params[filter.fieldName] = filter.value;
    });
  }

  getRequestParams(queryParams, params);

  return client.post(
    'case/export',
    {},
    {
      params,
    },
  );
};

export const useExportCasesCsv = () => {
  return useMutation({
    mutationFn: exportCasesCsv,
    onSuccess: (response) => {
      const reportData = response.data;
      console.log('Case report enqueued:', reportData);
    },
  });
};
